/**
 * DatePicker Component (shadcn/ui pattern)
 *
 * Combines Calendar + Popover for a rich date picking experience.
 * Supports single date and datetime modes.
 * Locale pt-BR by default via Calendar component.
 *
 * @module components/ui
 */

import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './Button';
import { Calendar } from './Calendar';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';

interface DatePickerProps {
  /** Currently selected date */
  value?: Date | null;
  /** Callback when date changes */
  onChange?: (date: Date | null) => void;
  /** Label text displayed above the picker */
  label?: string;
  /** Error message displayed below the picker */
  error?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Make the picker read-only */
  readOnly?: boolean;
  /** Placeholder text when no date is selected */
  placeholder?: string;
  /** Show time input alongside date */
  showTime?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** HTML id attribute */
  id?: string;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Date display format (date-fns format string) */
  formatStr?: string;
}

export function DatePicker({
  value,
  onChange,
  label,
  error,
  disabled = false,
  readOnly = false,
  placeholder,
  showTime = false,
  className,
  id,
  minDate,
  maxDate,
  formatStr,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [timeValue, setTimeValue] = useState(() => {
    if (value && showTime) {
      return format(value, 'HH:mm');
    }
    return '00:00';
  });

  const inputId = id || `datepicker-${Math.random().toString(36).substr(2, 9)}`;

  const displayFormat = formatStr || (showTime ? "dd/MM/yyyy 'às' HH:mm" : 'dd/MM/yyyy');

  const handleSelect = (selected: Date | undefined) => {
    if (!selected) {
      onChange?.(null);
      return;
    }

    if (showTime) {
      const [hours, minutes] = timeValue.split(':').map(Number);
      selected.setHours(hours, minutes, 0, 0);
    }

    onChange?.(selected);
    if (!showTime) {
      setOpen(false);
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value;
    setTimeValue(time);

    if (value) {
      const [hours, minutes] = time.split(':').map(Number);
      const newDate = new Date(value);
      newDate.setHours(hours, minutes, 0, 0);
      onChange?.(newDate);
    }
  };

  const handleClear = () => {
    onChange?.(null);
    setOpen(false);
  };

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-foreground mb-1"
        >
          {label}
        </label>
      )}
      <Popover open={open} onOpenChange={readOnly ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={inputId}
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-start text-left font-normal h-9',
              !value && 'text-muted-foreground',
              error && 'border-destructive focus-visible:ring-destructive'
            )}
            onClick={() => !readOnly && setOpen(true)}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            {value ? (
              format(value, displayFormat, { locale: ptBR })
            ) : (
              <span>{placeholder || 'Selecione uma data'}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value || undefined}
            onSelect={handleSelect}
            disabled={(date) => {
              if (minDate && date < minDate) return true;
              if (maxDate && date > maxDate) return true;
              return false;
            }}
            autoFocus
          />
          {showTime && (
            <div className="border-t border-border p-3 flex items-center gap-2">
              <label className="text-sm text-muted-foreground whitespace-nowrap">
                Horario:
              </label>
              <input
                type="time"
                value={timeValue}
                onChange={handleTimeChange}
                className={cn(
                  'flex h-8 w-full rounded-lg border border-input bg-transparent px-2 py-1 text-sm',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                )}
              />
            </div>
          )}
          {value && (
            <div className="border-t border-border p-2 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="text-xs text-muted-foreground"
              >
                Limpar
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
}

DatePicker.displayName = 'DatePicker';

export default DatePicker;
